import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api';
import { DIALOG_TOOLS, dialogsApi, type DialogDetail, type DialogFilter, type DialogListResult, type DialogRow } from '../api/dialogs';
import { tasksApi, type TaskBusiness } from '../api/tasks';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import { fmtWhen, statusLabel } from './tasks.format';

const PAGE_SIZE = 25;
const CHIPS: DialogFilter[] = ['working', 'waiting_human', 'stale', 'done'];

/** One state per row: "stale" wins over the stored status, because it is what the human must see. */
function stateOf(d: DialogRow): DialogFilter {
  return d.stale ? 'stale' : d.status;
}

function badgeClass(s: DialogFilter): string {
  return s === 'waiting_human' ? 'badge warn' : s === 'stale' ? 'badge danger' : s === 'done' ? 'badge ok' : 'badge';
}

/** The folder without the home prefix: enough to recognise it in a narrow column. */
function shortFolder(cwd: string | null): string | null {
  if (!cwd) return null;
  const parts = cwd.split('/').filter(Boolean);
  return parts.slice(-2).join('/');
}

function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : '';
  return code === 'dialog_not_found' ? t('dialogs.notFound') : code || t('common.error');
}

function selectedFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('id');
}

/** Розмови: every conversation with an assistant (ADR-0007). Hooks write it; the panel only reads. */
export function Dialogs() {
  const [businesses, setBusinesses] = useState<TaskBusiness[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [tool, setTool] = useState('');
  const [chip, setChip] = useState<DialogFilter | 'all'>('all');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<DialogListResult | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(selectedFromUrl);
  const [detail, setDetail] = useState<DialogDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    tasksApi.businesses().then(setBusinesses).catch((e) => setError(errorText(e)));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [businessId, tool, chip]);

  useEffect(() => {
    setError(null);
    const mine = ++seq.current;
    dialogsApi
      .list({ businessId: businessId || undefined, tool: (tool || undefined) as DialogRow['tool'] | undefined, status: chip === 'all' ? undefined : chip, page, pageSize: PAGE_SIZE })
      .then((r) => {
        if (mine === seq.current) setResult(r);
      })
      .catch((e) => {
        if (mine === seq.current) setError(errorText(e));
      });
  }, [businessId, tool, chip, page]);

  // The selection lives in the address (`?id=`) so a task born from a dialog can link straight to it.
  useEffect(() => {
    window.history.replaceState({}, '', selectedId ? `/dialogs?id=${encodeURIComponent(selectedId)}` : '/dialogs');
    setDetail(null);
    if (selectedId) dialogsApi.get(selectedId).then(setDetail).catch((e) => setError(errorText(e)));
  }, [selectedId]);

  const columns = useMemo<Column<DialogRow>[]>(
    () => [
      {
        key: 'title',
        title: t('dialogs.col.title'),
        wrap: true,
        render: (d) => (
          <>
            <a
              href={`/dialogs?id=${encodeURIComponent(d.id)}`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                setSelectedId(d.id);
                window.scrollTo({ top: 0 });
              }}
            >
              {d.title}
            </a>
            {d.cwd && (
              <>
                <br />
                <span className="muted" title={d.cwd}>{shortFolder(d.cwd)}</span>
              </>
            )}
          </>
        ),
      },
      { key: 'tool', title: t('dialogs.col.tool'), render: (d) => t(`dialogs.tool.${d.tool}`) },
      { key: 'business', title: t('dialogs.col.business'), render: (d) => d.business?.name ?? t('dialogs.noBusiness') },
      { key: 'status', title: t('dialogs.col.status'), render: (d) => <span className={badgeClass(stateOf(d))}>{t(`dialogs.status.${stateOf(d)}`)}</span> },
      { key: 'lastActivity', title: t('dialogs.col.lastActivity'), render: (d) => <span className="muted">{fmtWhen(d.lastActivityAt)}</span> },
      { key: 'questions', title: t('dialogs.col.questions'), render: (d) => <span className="num">{d.openQuestions}</span> },
    ],
    [],
  );

  const total = result?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const counts = result?.counts;
  const allCount = counts ? counts.working + counts.waiting_human + counts.done : null;
  const resume = detail?.tool === 'claude_code' && detail.cwd ? `cd "${detail.cwd}" && claude --resume ${detail.externalId}` : null;

  return (
    <div className="dialogs">
      <h1>{t('dialogs.title')}</h1>
      <p className="muted">{t('dialogs.intro')}</p>
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}

      {detail && (
        <section className="card" style={{ margin: 'var(--sp-4) 0' }} aria-label={detail.title}>
          <div className="row between">
            <h2 style={{ margin: 0 }}>{detail.title}</h2>
            <button type="button" className="btn secondary small" onClick={() => setSelectedId(null)}>{t('dialogs.detail.close')}</button>
          </div>
          <p className="muted">
            {t(`dialogs.tool.${detail.tool}`)}
            {detail.business ? ` · ${detail.business.name}` : ''} · {t('dialogs.detail.started', { when: fmtWhen(detail.startedAt) })} · {t('dialogs.detail.lastActivity', { when: fmtWhen(detail.lastActivityAt) })} ·{' '}
            <span className={badgeClass(stateOf(detail))}>{t(`dialogs.status.${stateOf(detail)}`)}</span>
          </p>
          {detail.cwd && <p className="muted wrap" style={{ overflowWrap: 'anywhere' }}>{t('dialogs.detail.folder', { folder: detail.cwd })}</p>}
          {detail.url && (
            <p>
              <a href={detail.url} target="_blank" rel="noreferrer">{t('dialogs.detail.open')}</a>
            </p>
          )}
          {detail.lastMessage && (
            <>
              <p><b>{t('dialogs.detail.lastMessage')}</b></p>
              <pre className="codeblock">{detail.lastMessage}</pre>
            </>
          )}
          <p><b>{t('dialogs.detail.tasks')}</b></p>
          {detail.tasks.length === 0 ? (
            <p className="muted">{t('dialogs.detail.noTasks')}</p>
          ) : (
            detail.tasks.map((task) => (
              <p key={task.id}>
                <Link to={`/tasks/${task.id}`}>{task.key}</Link> {task.title} <span className="badge">{statusLabel(task.status as Parameters<typeof statusLabel>[0])}</span>
              </p>
            ))
          )}
          {resume && <p className="muted wrap" style={{ overflowWrap: 'anywhere' }}>{t('dialogs.detail.resume', { command: resume })}</p>}
        </section>
      )}

      <div className="row filters" style={{ margin: 'var(--sp-4) 0 var(--sp-2)' }}>
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('dialogs.filter.allBusinesses')}>
          <option value="">{t('dialogs.filter.allBusinesses')}</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <select value={tool} onChange={(e) => setTool(e.target.value)} aria-label={t('dialogs.filter.allTools')}>
          <option value="">{t('dialogs.filter.allTools')}</option>
          {DIALOG_TOOLS.map((x) => (
            <option key={x} value={x}>{t(`dialogs.tool.${x}`)}</option>
          ))}
        </select>
      </div>

      <div className="row" style={{ marginBottom: 'var(--sp-3)' }}>
        <button type="button" className={`chip${chip === 'all' ? ' on' : ''}`} onClick={() => setChip('all')} aria-pressed={chip === 'all'}>
          {t('dialogs.chip.all')}
          {allCount !== null ? ` · ${allCount}` : ''}
        </button>
        {CHIPS.map((s) => (
          <button key={s} type="button" className={`chip${chip === s ? ' on' : ''}`} onClick={() => setChip(s)} aria-pressed={chip === s}>
            {t(`dialogs.status.${s}`)}
            {counts ? ` · ${counts[s]}` : ''}
          </button>
        ))}
      </div>

      <Table columns={columns} rows={result?.rows ?? []} rowKey={(d) => d.id} empty={result === null ? t('common.loading') : t('dialogs.empty')} />

      <div className="row" style={{ marginTop: 'var(--sp-3)', justifyContent: 'space-between' }}>
        <span className="muted">{t('dialogs.shown', { shown: result?.rows.length ?? 0, total, page })}</span>
        {pages > 1 && (
          <span className="row">
            <button type="button" className="btn secondary small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('dialogs.page.prev')}</button>
            <button type="button" className="btn secondary small" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>{t('dialogs.page.next')}</button>
          </span>
        )}
      </div>
    </div>
  );
}
